// src/components/scenes/GreetingScene.js
// Filepath: // src/components/scenes/GreetingScene.js

import React, { useEffect, useMemo } from "react";
import { Box, Avatar, Typography, Stack, Alert, CircularProgress } from "@mui/material";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import { useScene } from "../../state/SceneContext";
import { ensureThirdPartySyncForCurrentUser } from "../../services/authService";
import { useHomeDataStore } from "../../state/homeDataStore";

/**
 * Zustand-powered GreetingScene:
 *  - Calls useHomeDataStore().refresh() to load profile, holdings, free content, and activity
 *  - If navigated with params.reason === "refresh", show “updating” copy and force a fresh fetch
 *  - On refresh, we also instruct HomeScene to clear row memory and start at row/col 0
 *  - Proceeds to Home after a minimum delay or a safety timeout
 */

export default function GreetingScene() {
  const { scene, setScene } = useScene();

  const profileState = useHomeDataStore((s) => s.profile);
  const holdingsState = useHomeDataStore((s) => s.holdings);
  const freeState = useHomeDataStore((s) => s.free);
  const activityState = useHomeDataStore((s) => s.activity);
  const refresh = useHomeDataStore((s) => s.refresh);

  const hasAnyError =
    profileState.status === "error" ||
    holdingsState.status === "error" ||
    freeState.status === "error" ||
    activityState.status === "error";

  const AVATAR_SIZE = useMemo(() => Math.round(1920 / 7), []);
  const isNonEmpty = (v) => typeof v === "string" && v.trim().length > 0;

  const fullName = isNonEmpty(profileState.data?.fullName)
    ? profileState.data.fullName.trim()
    : "";
  const profilePic = isNonEmpty(profileState.data?.profilePicture)
    ? profileState.data.profilePicture.trim()
    : "";

  const cameFromRefresh = scene?.params?.reason === "refresh";

  const headingText = cameFromRefresh ? "Updating your library" : "Welcome to Kbean TV";
  const subText = cameFromRefresh
    ? "Fetching your latest purchases, collections, and activity…"
    : "Preparing your experience…";

  useEffect(() => {
    let cancelled = false;

    const MIN_DELAY_MS = 3000;
    const MAX_SAFETY_MS = 5000;

    const thirdParty = ensureThirdPartySyncForCurrentUser().catch(() => {});
    const preload = refresh({ force: !!cameFromRefresh }).catch(() => {});

    const minDelay = new Promise((r) => setTimeout(r, MIN_DELAY_MS));
    const safety = new Promise((r) => setTimeout(r, MAX_SAFETY_MS));

    Promise.race([
      Promise.all([thirdParty, preload, minDelay]).then(() => "ready"),
      safety.then(() => "safety"),
    ]).then(() => {
      if (cancelled) return;

      // On refresh, tell HomeScene to wipe any row/col memory and start at 0,0
      setScene({
        name: "home",
        params: {
          resetRowMemory: true,
          forceRow0Col0: cameFromRefresh === true, // true when we arrived here via Refresh
        },
      });
    });

    return () => {
      cancelled = true;
    };
  }, [refresh, setScene, cameFromRefresh]);

  return (
    <Box
      className="scene"
      sx={{
        height: "100vh",
        width: "100%",
        display: "grid",
        placeItems: "center",
        px: 2,
      }}
    >
      <Stack spacing={3} alignItems="center" sx={{ textAlign: "center", maxWidth: 1080 }}>
        <Avatar
          src={profilePic || undefined}
          alt={fullName || "User"}
          sx={{
            width: AVATAR_SIZE,
            height: AVATAR_SIZE,
            border: "10px solid #ffffff",
            boxShadow: 6,
            bgcolor: "#f5f5f5",
          }}
        >
          {!profilePic && <PersonOutlineOutlinedIcon sx={{ fontSize: AVATAR_SIZE * 0.45 }} />}
        </Avatar>

        <Typography variant="h2" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
          {headingText}
        </Typography>

        {isNonEmpty(fullName) && !cameFromRefresh && (
          <Typography variant="h4" sx={{ fontWeight: 600, lineHeight: 1.2, opacity: 0.95 }}>
            {fullName}
          </Typography>
        )}

        <Stack alignItems="center" spacing={1.5} sx={{ mt: 1 }}>
          <CircularProgress size={40} thickness={4} />
          <Typography variant="h6" sx={{ opacity: 0.9 }}>
            {subText}
          </Typography>
        </Stack>

        {hasAnyError && (
          <Alert
            severity="warning"
            sx={{
              mt: 2,
              bgcolor: "warning.light",
              color: "warning.contrastText",
              fontSize: 16,
            }}
          >
            We could not load everything just now. You can continue, but some content may be limited.
          </Alert>
        )}
      </Stack>
    </Box>
  );
}
